/**
 * payPeriodEngine.js — Pure functions for pay period aggregation
 *
 * No React, no hooks, no side effects.
 * Aggregates job earnings, expenses, hours into per-person summaries.
 */

import { normalizeSnapshot, getAllEarnings } from './calcEngine';

// ── Constants ────────────────────────────────────────────────────────────

export const PAYMENT_METHODS = ['Venmo', 'Zelle', 'Gusto', 'Check', 'Cash', 'Other'];

// Statuses where work has been done and crew has earned their allocation
export const WORK_DONE_STATUSES = [
  'shooting', 'post_production', 'partial_delivery',
  'delivered', 'invoiced', 'paid', 'archived',
];

const ROLE_LABELS = { orig: 'Originator', prod: 'Producer', post: 'Post' };

// ── Helpers ──────────────────────────────────────────────────────────────

function toDateStr(val) {
  if (!val) return '';
  if (typeof val === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(val)) return val;
  try {
    const d = new Date(val);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  } catch { return ''; }
}

function todayStr() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// ── Period Date Computation ──────────────────────────────────────────────

export function computeOpenPeriodDates(archivedPeriods) {
  const today = todayStr();

  if (!archivedPeriods || archivedPeriods.length === 0) {
    const now = new Date();
    const firstOfMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
    return { startDate: firstOfMonth, endDate: today, startsFuture: false };
  }

  // Find the most recent archived period by end_date
  const sorted = [...archivedPeriods].sort((a, b) =>
    (b.endDate || b.end_date || '').localeCompare(a.endDate || a.end_date || '')
  );
  const lastEnd = sorted[0].endDate || sorted[0].end_date;
  if (!lastEnd) {
    const now = new Date();
    const firstOfMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
    return { startDate: firstOfMonth, endDate: today, startsFuture: false };
  }

  // Start = day after last archived period ended
  const nextDay = new Date(lastEnd + 'T12:00:00');
  nextDay.setDate(nextDay.getDate() + 1);
  const startDate = `${nextDay.getFullYear()}-${String(nextDay.getMonth() + 1).padStart(2, '0')}-${String(nextDay.getDate()).padStart(2, '0')}`;
  const startsFuture = startDate > today;

  return { startDate, endDate: today, startsFuture };
}

// ── Disbursed Job Tracking ───────────────────────────────────────────────

/**
 * Collect job IDs already disbursed in archived periods.
 * Used to prevent double-counting across pay periods.
 */
export function getDisbursedJobIds(archivedPeriods) {
  const ids = new Set();
  (archivedPeriods || []).forEach(p => {
    (p.jobIds || p.job_ids || []).forEach(id => ids.add(id));
  });
  return ids;
}

// ── Earnings from Jobs ───────────────────────────────────────────────────

/**
 * Work-basis earnings: includes any job where the calculator was
 * explicitly saved (savedAt exists) and the job status indicates
 * work has been done. Bucketed by savedAt date.
 *
 * @param {Array} jobs
 * @param {string} startDate — YYYY-MM-DD
 * @param {string} endDate — YYYY-MM-DD
 * @param {Set} [disbursedJobIds] — job IDs already in archived periods
 * @returns {object} — { personName: { earnings, projects[] } }
 */
export function getPeriodEarnings(jobs, startDate, endDate, disbursedJobIds = new Set()) {
  const people = {};

  if (!jobs || !startDate || !endDate) return people;

  const qualifying = jobs.filter(j => {
    if (!j.calcSnapshot) return false;
    const snap = normalizeSnapshot(j.calcSnapshot);
    if (!snap.savedAt) return false;
    if (!WORK_DONE_STATUSES.includes(j.status)) return false;
    if (disbursedJobIds.has(j.id)) return false;
    const savedDate = toDateStr(snap.savedAt);
    return savedDate >= startDate && savedDate <= endDate;
  });

  qualifying.forEach(j => {
    const snap = normalizeSnapshot(j.calcSnapshot);
    const earnings = getAllEarnings(snap);

    earnings.forEach(e => {
      if (!people[e.name]) people[e.name] = { earnings: 0, projects: [] };
      people[e.name].earnings += e.earned;
      people[e.name].projects.push({
        title: j.title || 'Untitled',
        amount: e.earned,
        role: e.role,
        roleLabel: ROLE_LABELS[e.role] || e.role,
      });
    });
  });

  return people;
}

/**
 * Get the IDs of jobs qualifying for a period's earnings.
 * Used at archive time to record which jobs were disbursed.
 */
export function getQualifyingJobIds(jobs, startDate, endDate, disbursedJobIds = new Set()) {
  if (!jobs || !startDate || !endDate) return [];
  return jobs
    .filter(j => {
      if (!j.calcSnapshot) return false;
      const snap = normalizeSnapshot(j.calcSnapshot);
      if (!snap.savedAt) return false;
      if (!WORK_DONE_STATUSES.includes(j.status)) return false;
      if (disbursedJobIds.has(j.id)) return false;
      const savedDate = toDateStr(snap.savedAt);
      return savedDate >= startDate && savedDate <= endDate;
    })
    .map(j => j.id);
}

// ── Expenses ─────────────────────────────────────────────────────────────

export function getPeriodExpenses(expenses, startDate, endDate) {
  const people = {};

  if (!expenses || !startDate || !endDate) return people;

  expenses.forEach(e => {
    const d = toDateStr(e.date);
    if (!d || d < startDate || d > endDate) return;
    if ((e.person || '').toLowerCase() === 'company') return; // Company pays directly — no reimbursement

    const person = e.person || 'Unknown';
    if (!people[person]) people[person] = { expenses: 0, expenseItems: [] };
    people[person].expenses += (e.amount || 0);
    people[person].expenseItems.push({
      desc: e.description || e.category || 'Expense',
      amount: e.amount || 0,
      date: d,
    });
  });

  return people;
}

// ── Hours ────────────────────────────────────────────────────────────────

export function getPeriodHours(timeEntries, startDate, endDate) {
  const people = {};

  if (!timeEntries || !startDate || !endDate) return people;

  timeEntries.forEach(e => {
    const d = toDateStr(e.date);
    if (!d || d < startDate || d > endDate) return;

    const person = e.person || e.worker || 'Unknown';
    people[person] = (people[person] || 0) + (e.hours || 0);
  });

  return people;
}

// ── Pay Type Lookup ──────────────────────────────────────────────────────

export function getPayTypeLookup(contacts) {
  const lookup = {};
  if (!contacts) return lookup;

  contacts.forEach(c => {
    const type = c.type || (c.data && c.data.type);
    if (type !== 'crew') return;
    const name = c.name;
    if (!name) return;
    // payType can be in top-level or nested in data JSONB
    const payType = c.payType || c.pay_type || (c.data && c.data.payType) || '1099';
    lookup[name] = payType;
  });

  return lookup;
}

// ── Total Paid ───────────────────────────────────────────────────────────

export function totalPaid(paymentData) {
  if (!paymentData || !paymentData.payments) return 0;
  return paymentData.payments.reduce((s, p) => s + (p.amount || 0), 0);
}

// ── Build People Summary ─────────────────────────────────────────────────

export function buildPeopleSummary(earningsMap, expensesMap, hoursMap, payTypeMap, paymentsMap) {
  const allNames = new Set([
    ...Object.keys(earningsMap),
    ...Object.keys(expensesMap),
    ...Object.keys(hoursMap),
  ]);

  const people = [];

  allNames.forEach(name => {
    const earn = earningsMap[name] || { earnings: 0, projects: [] };
    const exp = expensesMap[name] || { expenses: 0, expenseItems: [] };
    const hours = hoursMap[name] || 0;
    const payType = payTypeMap[name] || '1099';
    const paymentData = paymentsMap[name] || { payments: [], closed: false };

    // Migrate old single-payment format
    if (!paymentData.payments && paymentData.method) {
      paymentData.payments = [{ method: paymentData.method, date: paymentData.date, amount: paymentData.amount, note: paymentData.note }];
    }
    if (!paymentData.payments) paymentData.payments = [];

    const gross = earn.earnings + exp.expenses;
    const paid = totalPaid(paymentData);
    const actualTarget = paymentData.actual || gross;
    const remaining = actualTarget - paid;

    const isClosed = !!paymentData.closed;
    const hasPayments = paymentData.payments.length > 0;
    const status = isClosed ? 'closed' : hasPayments ? 'partial' : 'unpaid';

    people.push({
      name,
      earnings: earn.earnings,
      expenses: exp.expenses,
      hours,
      projects: earn.projects,
      expenseItems: exp.expenseItems,
      payType,
      gross,
      paid,
      actualTarget,
      remaining,
      status,
      paymentData,
    });
  });

  return people.sort((a, b) => a.name.localeCompare(b.name));
}

// ── Build payments map from DB rows ──────────────────────────────────────

export function buildPaymentsMap(paymentRows, periodKey) {
  const map = {};
  if (!paymentRows) return map;

  paymentRows.forEach(row => {
    const key = row.periodKey || row.period_key;
    if (key !== periodKey) return;
    const name = row.personName || row.person_name;
    if (!name) return;
    map[name] = {
      id: row.id,
      payments: row.payments || [],
      closed: !!row.closed,
      actual: row.actual,
    };
  });

  return map;
}

// ── Shared helpers (used by Financials & Crew Earnings tabs) ────────────

/**
 * Filter jobs by a date window (shootDate, falling back to createdAt),
 * excluding specified statuses (default: void + archived).
 */
export function filterJobsByDateRange(jobs, startDate, endDate, excludeStatuses = ['void', 'archived']) {
  if (!jobs) return [];
  return jobs.filter((j) => {
    if (excludeStatuses.includes(j.status)) return false;
    const d = j.shootDate || (j.createdAt ? j.createdAt.split('T')[0] : '');
    if (startDate && d && d < startDate) return false;
    if (endDate && d && d > endDate) return false;
    return true;
  });
}

/**
 * Aggregate crew earnings + company share from an array of jobs.
 * @returns { crewCosts: { [name]: total }, totalCrewCost, rfShare }
 */
export function aggregateCrewFromJobs(jobs) {
  const crewCosts = {};
  let totalCrewCost = 0;
  let rfShare = 0;

  (jobs || []).forEach((j) => {
    const ns = normalizeSnapshot(j.calcSnapshot);
    rfShare += ns.amounts.company;
    getAllEarnings(ns).forEach((e) => {
      totalCrewCost += e.earned;
      crewCosts[e.name] = (crewCosts[e.name] || 0) + e.earned;
    });
  });

  return { crewCosts, totalCrewCost, rfShare };
}

